import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { tx as txUrl } from "../lib/explorer";
import type { TxPhase } from "../hooks/useTxFlow";

interface TxToastProps {
  phase: TxPhase;
  hash: `0x${string}` | undefined;
  label: string;
  error: string;
  onClose: () => void;
}

const toneByPhase: Record<TxPhase, string> = {
  idle: "",
  wallet: "border-l-wing",
  pending: "border-l-wing",
  success: "border-l-leaf",
  rejected: "border-l-ember",
  reverted: "border-l-ember",
  error: "border-l-ember",
};

const suffixByPhase: Record<TxPhase, string> = {
  idle: "",
  wallet: "请在钱包确认",
  pending: "上链中",
  success: "成功",
  rejected: "钱包拒绝",
  reverted: "链上回滚",
  error: "失败",
};

function PhaseIcon({ phase }: { phase: TxPhase }) {
  switch (phase) {
    case "wallet":
    case "pending":
      return <Loader2 className="w-5 h-5 text-wing animate-spin" aria-hidden />;
    case "success":
      return <CheckCircle2 className="w-5 h-5 text-leaf" aria-hidden />;
    case "rejected":
      return <X className="w-5 h-5 text-ember" aria-hidden />;
    case "reverted":
    case "error":
      return <AlertTriangle className="w-5 h-5 text-ember" aria-hidden />;
    default:
      return null;
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function TxToast({ phase, hash, label, error, onClose }: TxToastProps) {
  const [entered, setEntered] = useState(false);

  // Slide-in transition: mount with translate, then flip on next frame.
  useEffect(() => {
    if (phase === "idle") {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [phase]);

  // Auto-dismiss success after 4s.
  useEffect(() => {
    if (phase !== "success") return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [phase, onClose]);

  if (phase === "idle") return null;

  const showHashLink = !!hash && (phase === "pending" || phase === "success");
  const showError = (phase === "error" || phase === "reverted") && !!error;

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "glass border-l-4",
        toneByPhase[phase],
        "fixed top-4 right-4 z-50 max-w-sm w-[22rem] p-3 pr-10",
        "flex items-start gap-3",
        "transition-all duration-200 ease-out",
        entered ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0",
      ].join(" ")}
    >
      <div className="shrink-0 mt-0.5">
        <PhaseIcon phase={phase} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-ink">
          <span>{label}</span>
          <span className="text-ink-dim font-normal"> · {suffixByPhase[phase]}</span>
        </div>

        {showHashLink && hash && (
          <a
            href={txUrl(hash)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-wing hover:text-wing/80 transition-colors"
          >
            <ExternalLink className="w-3 h-3" aria-hidden />
            <span>View on BscScan</span>
          </a>
        )}

        {showError && (
          <div className="mt-1 text-xs text-ink-dim break-words">
            {truncate(error, 80)}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label="关闭通知"
        className="absolute top-2 right-2 text-ink-dim hover:text-ink transition-colors p-1"
      >
        <X className="w-4 h-4" aria-hidden />
      </button>
    </div>
  );
}
